<template>
  <app-box class="app-task-progress no-mar-top" :class="statusClass">
    <div class="app-task-progress-title text-muted mar-bottom-half" v-html="$t('job-status-of', { task: taskDisplayName })" />
    <template v-if="!isFinished && (showProgress || isFailed)">
      <template v-if="hasStepProgress">
        <b-progress :max="maxSteps" height="1.5rem">
          <div class="app-task-progress-bar-text">
            <span class="app-task-progress-bar-info">{{ currentStepText }}</span>
            <span class="app-task-progress-bar-steps">{{ stepCountText }}</span>
          </div>
          <b-progress-bar :value="currentStep" variant="success" />
        </b-progress>
      </template>

      <template v-else>
        <div class="app-task-progress-spinner">
          <app-spinner :small="true" />
          <span v-if="lastInfo" class="pad-left-half">{{ lastInfo }}</span>
        </div>
      </template>
    </template>
    <template v-else>
      <div v-if="isFailed" class="text-danger">
        <b>
          <app-icon icon="x-octagon" /> {{ $t("failed") }}: {{ errorText }}
        </b>
      </div>
      <div v-if="isSuccessful" class="text-success">
        <b>
          <app-icon icon="check2" /> {{ $t("successfully-finished") }}
        </b>
      </div>
    </template>

    <div v-if="hasLogInfos" class="app-task-progress-toggle">
      <app-link @click="expanded = !expanded">{{ expanded ? $t("hide-log") : $t("show-log") }}</app-link>
    </div>

    <div v-if="expanded && hasLogInfos" class="app-task-progress-log">
      <div v-for="(info, index) in infos" :key="index" class="app-task-progress-log-entry">
        > {{ info }}
      </div>
    </div>
  </app-box>
</template>

<script lang="ts">
import { Component, Prop } from "vue-property-decorator";
import { TaskSchema } from "@/app/shared/services/volateq-api/api-schemas/task-schema";
import { BaseComponent } from "../base-component/base-component";
import AppSpinner from "../app-spinner/app-spinner.vue";
import AppLink from "../app-link/app-link.vue";
import AppBox from "../app-box/app-box.vue";
import AppIcon from "../app-icon/app-icon.vue";

@Component({
  name: "app-task-progress",
  components: {
    AppSpinner,
    AppLink,
    AppBox,
    AppIcon,
  },
})
export default class AppTaskProgress extends BaseComponent {
  @Prop({ required: true }) task!: TaskSchema;
  @Prop({ default: true }) showProgress!: boolean;

  expanded = false;

  get taskDisplayName(): string {
    const taskName = this.$t(this.task.name).toString();
    return this.task.evaluation_batch_num == null
      ? taskName
      : `${taskName} EB${this.task.evaluation_batch_num}`;
  }

  get infos(): string[] {
    return this.task.output?.infos || [this.$t("wait-for-start").toString()];
  }

  get isSuccessful(): boolean {
    return this.task.state === "SUCCESSFUL";
  }

  get isFailed(): boolean {
    return this.task.state === "FAILED";
  }

  get isFinished(): boolean {
    return this.isSuccessful || this.isFailed;
  }

  get statusClass(): string {
    if (this.isSuccessful) {
      return "is-success";
    }
    if (this.isFailed) {
      return "is-failed";
    }

    return "";
  }

  get progressVariant(): "primary" | "success" | "danger" {
    if (this.isSuccessful) {
      return "success";
    }
    if (this.isFailed) {
      return "danger";
    }

    return "primary";
  }

  get hasInfos(): boolean {
    return this.infos.length > 0;
  }

  get hasLogInfos(): boolean {
    return this.infos.length > 1;
  }

  get lastInfo(): string | null {
    return this.hasInfos ? this.cutTimestamp(this.infos[this.infos.length - 1]) : null;
  }

  get maxSteps(): number {
    return this.task.output?.max_steps || 0;
  }

  get hasStepProgress(): boolean {
    return this.maxSteps > 0;
  }

  get currentStep(): number {
    if (!this.hasStepProgress) {
      return 0;
    }

    const step = this.task.output?.step || 0;
    return Math.max(0, Math.min(step, this.maxSteps));
  }

  get currentStepText(): string {
    return `${this.lastInfo || ""}`
  }

  get stepCountText(): string {
    if (!this.hasStepProgress) {
      return "";
    }
    return this.$t("step-x-of-y", { current: this.currentStep, total: this.maxSteps }).toString();
  }

  get errorText(): string {
    return this.task.output?.error || "";
  }

  private cutTimestamp(msg: string): string {
    const timestampPattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} - /;
    return msg.replace(timestampPattern, '');
  }
}
</script>

<style lang="scss">
.app-task-progress {
  &-spinner {
    display: flex;
    align-items: center;
  }

  &-status-text {
    padding: 0.35rem 0.6rem;
    border: 1px solid var(--default-border-color);
    background-color: var(--muted-bg-color);
  }

  &.is-success &-status-text {
    border-color: var(--success-border-color, var(--primary-border-color));
    color: var(--success-text-color, var(--primary-text-color));
  }

  &.is-failed &-status-text {
    border-color: var(--danger-border-color, var(--danger-text-color));
    color: var(--danger-text-color);
  }

  &-bar-text {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5em;
    width: 100%;
    height: 100%;
    line-height: 1.75em;
    padding: 0 0.5em;
  }

  &-bar-info {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 auto;
    min-width: 0;
  }

  &-bar-steps {
    white-space: nowrap;
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
    opacity: 0.85;
  }

  &-toggle {
    margin-top: 0.5rem;
  }

  &-log {
    margin-top: 0.5rem;
    max-height: 10rem;
    overflow: auto;
    border: 1px solid var(--default-border-color);
    padding: 0.5rem;
    color: var(--muted-text-color);
    font-size: 0.8em;
  }

  &-log-entry {
    white-space: pre-wrap;
    word-break: break-word;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
